import React, { useState, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import ButtonBase from '@material-ui/core/ButtonBase';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';

// CONSTANTS
import { ROUTE_SHOWS_AND_MOVIES_MOVIE, ROUTE_SHOWS_AND_MOVIES_SHOW } from '@/constants/routes';
import { ContentType } from '@/constants/watchList';

// UTILITY
import { analyticsInternalLinks } from '@/utility/analytics';
import { getLink, routeEnhance } from '@/utility/routes';
import { getYearRange, getContentYear } from '@/utility/content';

// HOOKS
import useDictionary from '@/hooks/useDictionary';

// COMPONENTS
import SwiperCarousel, { SwiperSlide } from '@/components/Carousels/SwiperCarousel';

// STYLES
import { Container } from './styles';

const renderGenres = (genres = []) => {
  return genres
    .map(g => g.name)
    .filter(Boolean)
    .join('/');
};

const BannerFeaturedContent = ({ data, title }) => {
  const [activeSlide, setActiveSlide] = useState();

  const paginationRef = useRef(null);
  const navigationNextRef = useRef(null);
  const navigationPrevRef = useRef(null);

  const [t] = useTranslation();
  const navigate = useNavigate();
  const genresData = useDictionary(activeSlide?.genres, 'genres');

  const handleActiveSlideChange = ({ realIndex }) => setActiveSlide(data?.[realIndex]);

  const dataHasLength = useMemo(() => data.length > 2, [data]);
  const activeSlideDate = useMemo(() => {
    if (activeSlide?.type === ContentType.SHOW) {
      return getYearRange(activeSlide.year_range, activeSlide.status, t('common.movie.current'));
    }

    return getContentYear(activeSlide?.first_air_date);
  }, [activeSlide]);

  const handleButtonClick = React.useCallback(() => {
    let routeEnhanced;
    switch (activeSlide.type) {
      case ContentType.MOVIE:
        routeEnhanced = routeEnhance(
          getLink(ROUTE_SHOWS_AND_MOVIES_MOVIE, {
            movie_id: activeSlide.slug || activeSlide.value,
          }),
        );
        break;

      case ContentType.SHOW:
        routeEnhanced = routeEnhance(
          getLink(ROUTE_SHOWS_AND_MOVIES_SHOW, { show_id: activeSlide.slug || activeSlide.id }),
        );
        break;

      default:
        break;
    }

    if (!routeEnhanced) {
      return;
    }

    analyticsInternalLinks({
      unique_id: routeEnhanced.state.unique_id,
      from_page_path: routeEnhanced.state.from_page_path,
      from_search_params: routeEnhanced.state.from_search_params,
      to_page_path: activeSlide.link,
      to_search_params: routeEnhanced.search,
      partner_id: routeEnhanced.state.partner_id,
      location: routeEnhanced.state.location,
    });

    navigate(routeEnhanced);
  }, [activeSlide, navigate]);

  const swiperOptions = {
    slidesPerView: dataHasLength ? 'auto' : 1,
    spaceBetween: 0,
    loop: dataHasLength,
    centeredSlides: true,
    threshold: 10,
    pagination: dataHasLength
      ? {
          el: paginationRef.current,
        }
      : false,
    onSlideChange: handleActiveSlideChange,
    onSwiper: handleActiveSlideChange,
    customNavigation: dataHasLength
      ? {
          nextEl: navigationNextRef.current,
          prevEl: navigationPrevRef.current,
        }
      : true,

    breakpoints: {
      960: {
        centeredSlides: false,
      },
    },
  };

  if (!data) return null;

  return (
    <Container className="m-b-30 section section--dark">
      <div className="content">
        <Typography variant="h3" className="content__title m-b-20">
          {title}
        </Typography>
        <Box display="flex" className="content__box">
          <div className="content-info">
            {activeSlide && (
              <>
                <Typography className="content-info__title" variant="h5">
                  {activeSlide?.title}
                </Typography>
                <Typography className="content-info__genres" color="textSecondary">
                  {renderGenres(genresData)}
                  {!!activeSlideDate && `, ${activeSlideDate}`}
                </Typography>
                <Typography className="content-info__description">
                  {activeSlide?.overview}
                </Typography>
                <Button
                  onClick={handleButtonClick}
                  color="primary"
                  fullWidth
                  className="content-info__btn"
                >
                  {t('buttons.seeDetails')}
                </Button>
              </>
            )}
          </div>
          <div className="content__carousel">
            <SwiperCarousel options={swiperOptions}>
              {data.map(e => {
                return (
                  <SwiperSlide key={e.id}>
                    <div className="slide-image-holder">
                      <img className="slide-image" src={e.thumbnail} alt={e.title} />
                    </div>
                  </SwiperSlide>
                );
              })}
            </SwiperCarousel>
          </div>
          {dataHasLength && (
            <Box display="flex" className="swiper-navigation">
              <ButtonBase
                ref={navigationPrevRef}
                className="swiper-button-nav swiper-button-prev"
                disableRipple
              >
                <KeyboardArrowLeftIcon />
              </ButtonBase>
              <Box ref={paginationRef} display="flex" alignItems="center" />
              <ButtonBase ref={navigationNextRef} className="swiper-button-nav swiper-button-next">
                <KeyboardArrowRightIcon />
              </ButtonBase>
            </Box>
          )}
        </Box>
      </div>
    </Container>
  );
};

BannerFeaturedContent.propTypes = {
  data: PropTypes.array,
  title: PropTypes.string,
};

export default React.memo(BannerFeaturedContent);
